import {
	AbstractControl,
	FormGroup,
	UntypedFormArray,
	UntypedFormGroup,
	ValidatorFn,
	Validators
}                                from '@angular/forms';
import { APP_CONFIG }            from '@appConfig/_app-config_master';
import { APP_MODE }              from '@appConfig/app-mode-config';
import { FormUtils }             from '@shared/forms/helpers/index';
import {
	FormValidatorConfigType,
	FormValidatorIdentifierEnum,
	FormValidatorsConfigMap,
	FormValidatorVariantEnum,
	IFormValidatorDateRelationConfig,
	IFormValidatorMaxLengthConfig,
	IFormValidatorMinLengthConfig,
	IFormValidatorPatternConfig,
	IFormValidatorRequiredConfig,
	IFormValidatorRequiredCustomConfig
}                                from '@shared/forms/models/form.model';
import { dateRelationValidator } from '@shared/forms/validators/date-relation-validator';
import { requiredCustom }        from '@shared/forms/validators/required-custom';
import { LoggerService }         from '@shared/services/logger.service';

export {
	fieldValidatorsAddByConfig,
	fieldValidatorsGetFormValidatorConfigByChildId,
	fieldValidatorsUpdateValidatorByChildId,
	findInvalidControlsRecursively,
	formControlGet,
	formControlSetValue,
	formControlValidatorCreate,
	formControlValidatorUpdate,
	hasFormInvalidFields,
	hasRequiredValidator
};


/**
 * Adds validator functions according to a config (`validatorsConfigMap`) to the
 * `FormControl`'s validators array reference.
 * This function applies changes to the `FormControl`'s validators reference
 *
 * @param validators - The FormControl's validators array reference
 * @param validatorsConfigMap - A map containing lists of form validator config
 *                        objects, according to which the validator functions
 *                        are added to the validators array reference
 * @param validatorVariant - Identifier of the validator variant.
 *                           For conditional validators, the validator function
 *                           will only be added if the condition is met.
 *
 * @return void
 */
function fieldValidatorsAddByConfig(
	validators: ValidatorFn[],
	validatorsConfigMap: FormValidatorsConfigMap,
	validatorVariant: FormValidatorVariantEnum
): void {
	validatorsConfigMap?.forEach( ( formValidatorConfigTypeList: FormValidatorConfigType[], formValidatorIdentifier: FormValidatorIdentifierEnum ) => {
		if ( !formValidatorConfigTypeList ) { return; }

		formValidatorConfigTypeList?.forEach( ( formValidatorConfigType: FormValidatorConfigType ) => {
			if ( formValidatorConfigType.variant?.type !== validatorVariant ) { return; }
			if (
				formValidatorConfigType.variant?.type === FormValidatorVariantEnum.CONDITIONAL
				&& !formValidatorConfigType.variant?.conditionIsValid
			) { return; }

			switch ( formValidatorIdentifier ) {
				case FormValidatorIdentifierEnum.DATE_RELATION:
					const formValidatorDateRelation: IFormValidatorDateRelationConfig = formValidatorConfigType as IFormValidatorDateRelationConfig;
					if ( formValidatorDateRelation?.isEnabled ) { validators.push( dateRelationValidator( formValidatorDateRelation?.config ) ); }
					break;

				case FormValidatorIdentifierEnum.MAX_LENGTH:
					const formValidatorMaxLength: IFormValidatorMaxLengthConfig = formValidatorConfigType as IFormValidatorMaxLengthConfig;
					if ( formValidatorMaxLength?.isEnabled ) { validators.push( Validators.maxLength( formValidatorMaxLength?.length ) ); }
					break;

				case FormValidatorIdentifierEnum.MIN_LENGTH:
					const formValidatorMinLength: IFormValidatorMinLengthConfig = formValidatorConfigType as IFormValidatorMinLengthConfig;
					if ( formValidatorMinLength?.isEnabled ) { validators.push( Validators.minLength( formValidatorMinLength?.length ) ); }
					break;

				case FormValidatorIdentifierEnum.PATTERN:
					const formValidatorPattern: IFormValidatorPatternConfig = formValidatorConfigType as IFormValidatorPatternConfig;
					if ( formValidatorPattern?.isEnabled ) { validators.push( Validators.pattern( formValidatorPattern?.pattern ) ); }
					break;

				case FormValidatorIdentifierEnum.REQUIRED:
					const formValidatorRequired: IFormValidatorRequiredConfig = formValidatorConfigType as IFormValidatorRequiredConfig;
					if ( formValidatorRequired?.isEnabled ) { validators.push( Validators.required ); }
					break;

				case FormValidatorIdentifierEnum.REQUIRED_CUSTOM:
					const formValidatorRequiredCustom: IFormValidatorRequiredCustomConfig = formValidatorConfigType as IFormValidatorRequiredCustomConfig;
					if ( formValidatorRequiredCustom?.isEnabled ) { validators.push( requiredCustom( formValidatorRequiredCustom.errorKey ) ); }
					break;
			}
		} );
	} );
}

/**
 * Helper function for getting a specific element ("child validator") of a
 * list of validator config objects in a validator config map
 *
 * @param formValidatorsConfigMap - The map that contains lists of validator config objects for a single form field (`FormControl`).
 * @param formValidatorIdentifier - Identifier of the validator config object list you are interested in
 * @param formValidatorIdentifierChild - Identifier of the config object list element ("child validator") | default value is `'default'`
 *
 * @return FormValidatorConfigType - If found, the child validator is returned, otherwise `undefined`
 */
function fieldValidatorsGetFormValidatorConfigByChildId(
	formValidatorsConfigMap: FormValidatorsConfigMap,
	formValidatorIdentifier: FormValidatorIdentifierEnum,
	formValidatorIdentifierChild?: string
): FormValidatorConfigType {
	const formValidatorTypeList: FormValidatorConfigType[] = formValidatorsConfigMap?.get( formValidatorIdentifier );

	return formValidatorTypeList?.find( ( formValidator: FormValidatorConfigType, index: number, validatorConfigList: FormValidatorConfigType[] ) => {

			if ( 'identifierChild' in formValidator ) {
				return formValidator.identifierChild === formValidatorIdentifierChild;
			}

			const hasValidatorConfigListMultipleElements = validatorConfigList.length > 1;

			if ( APP_CONFIG.appMode === APP_MODE.LOCAL && hasValidatorConfigListMultipleElements ) { // log only in local dev environment
				console.error(
					'FormUtils:fieldValidatorsGetFormValidatorTypeByChildId --',
					'Possible misconfiguration!',
					`Config list for validator type ${ formValidatorIdentifier } is longer than 1, but element doesn't have 'identifierChild' property!`
				);
			}

			return !hasValidatorConfigListMultipleElements;
		}
	);
}

/**
 * Helper function for updating a specific validator config object ("child validator").
 * This function applies changes to FormValidatorsConfigMap reference
 *
 * @param formValidatorsConfigMap - The map that contains lists of validator config objects for a single form field (`FormControl`).
 * @param formValidatorConfigUpdate - Data containing the changes for the child element. The changes are merged with the existing data.
 * @param formValidatorIdentifier - Identifier of the validator config object list you are interested in
 * @param formValidatorIdentifierChild - Identifier of the config object list element ("child validator") | default value is `'default'`
 *
 * @return FormValidatorConfigType - If found, the config object of the child validator is returned, otherwise `undefined`
 */
function fieldValidatorsUpdateValidatorByChildId<FormValidatorConfig extends FormValidatorConfigType>(
	formValidatorsConfigMap: FormValidatorsConfigMap,
	formValidatorConfigUpdate: Partial<FormValidatorConfig>,
	formValidatorIdentifier: FormValidatorIdentifierEnum,
	formValidatorIdentifierChild?: string
): FormValidatorConfigType {
	const formValidatorConfigType: FormValidatorConfig = FormUtils.fieldValidatorsGetFormValidatorConfigByChildId(
		formValidatorsConfigMap,
		formValidatorIdentifier,
		formValidatorIdentifierChild
	) as FormValidatorConfig;

	if ( !formValidatorConfigType ) { return; }
	Object.assign<FormValidatorConfig, FormValidatorConfig>( formValidatorConfigType, { ...formValidatorConfigType, ...formValidatorConfigUpdate } );

	return formValidatorConfigType;
}


function findInvalidControlsRecursively( formToInvestigate: UntypedFormGroup | UntypedFormArray ): string[] {
	const invalidControls: string[] = [];
	const recursiveFunc = ( form: UntypedFormGroup | UntypedFormArray ) => {
		Object.keys(
			form.controls )
		.forEach(
			field => {
				const control = form.get(
					field );
				if ( control.invalid ) {
					invalidControls.push(
						field );
					control.markAsTouched();
					control.markAsDirty();
				}
				if ( control instanceof
						 UntypedFormGroup ||
						 control instanceof
						 UntypedFormArray ) {
					recursiveFunc(
						control );
				}
			} );
	};

	recursiveFunc(
		formToInvestigate );
	return invalidControls;
}


function formControlGet<
	FormGroupValueType extends { [K in keyof FormGroupValueType]: AbstractControl },
	FormProp extends string
>( formGroup: FormGroup<FormGroupValueType>, formProp: FormProp ): AbstractControl {
	return formGroup?.get( formProp );
}

function formControlSetValue<
	FormGroupValueType extends { [K in keyof FormGroupValueType]: AbstractControl },
	FormProp extends keyof FormValues & string,
	FormValues
>( formGroup: FormGroup<FormGroupValueType>, formProp: FormProp, value: FormValues[typeof formProp] ): void {
	const control: AbstractControl = formControlGet( formGroup, formProp );
	control?.setValue( value );
}

/**
 * Manipulates particular FormControl of a FormGroup.
 *
 * @param data - holds props to specify validator creation
 * <ul>
 * <li> disableValidation flag to completely disable validation
 * <li> disableValidationForFields: array to selectively disable validation fields of FormGroup
 * <li> disableOnlyValidators: array of validators to disable when disable flag is set,
 * 	 disables all validators if null or undefined, keep all validators if empty array
 * <li> disableOnlyValidatorsCallbacks: array of callbacks to disable validators when disable flag is set,
 * 	 disables all callbacks if null or undefined, keep all callbacks if empty array
 * <li> formGroup: FormGroup reference
 * <li> formProp: FormControl identifier
 * <li> validatorsInitial: Array of validator functions to apply initially
 * <li> validatorsConditionalCallbacks: Array of callbacks with validator functions to apply conditionally
 * </ul>
 */
function formControlValidatorCreate<FormValueType extends { [K in keyof FormValueType]: AbstractControl }, PropType extends string>(
	data: {
		disableValidation?: boolean
		disableValidationForFields?: PropType[],
		disableOnlyValidators?: ValidatorFn[],
		disableOnlyValidatorsCallbacks?: Array<( abstractControlValidators: ValidatorFn[] ) => void>,
		formGroup: FormGroup<FormValueType>,
		formProp: PropType,
		validatorsInitial?: ValidatorFn[],
		validatorsConditionalCallbacks?: Array<( abstractControlValidators: ValidatorFn[] ) => void>,
	}
): void {
	const {
		disableValidation = false,
		disableValidationForFields = [],
		disableOnlyValidators = null,
		disableOnlyValidatorsCallbacks = null,
		formGroup,
		formProp,
		validatorsInitial = [],
		validatorsConditionalCallbacks
	} = { ...data };

	if ( !formGroup || !formProp ) { return; }

	const abstractControl: AbstractControl = formGroup?.get( formProp );

	// effective validators for final usage
	let validatorsResult: ValidatorFn[] = validatorsInitial;
	let validatorsCallbacksResult: Array<( abstractControlValidators: ValidatorFn[] ) => void> = validatorsConditionalCallbacks;

	// Resolve ALL disabling validations (FNs, callbacks)
	const isDisableValidation: boolean = disableValidationForFields.includes( formProp ) || disableValidation;
	if ( isDisableValidation ) {
		validatorsResult = [];
		validatorsCallbacksResult = [];

		// Validators FNs
		if ( disableOnlyValidators ) {						// remove only specified validators
			validatorsResult = validatorsInitial?.filter( ( validator: ValidatorFn ) => !disableOnlyValidators.includes( validator ) );
		}

		// Callbacks
		if ( disableOnlyValidatorsCallbacks ) {		// remove only specified callbacks
			validatorsCallbacksResult =
				validatorsConditionalCallbacks?.filter( ( validatorCallback ) => !disableOnlyValidatorsCallbacks.includes( validatorCallback ) );
		}
	}

	// Assign effective validators (FNs, callbacks)
	const abstractControlValidators: ValidatorFn[] = validatorsResult ?? [];
	validatorsCallbacksResult?.forEach( ( validatorCallback: ( abstractControlValidators: ValidatorFn[] ) => void ) => {
		validatorCallback( abstractControlValidators );
	} );

	formControlValidatorUpdate<PropType>( {
		abstractControl,
		abstractControlValidators,
		formProp
	} );
}

/**
 * Updates validators and validity state of a FormControl, expects that disabling of validators is already solved.
 *
 * @param data - holds props for updating the control
 *  abstractControl: FormControl reference
 *  abstractControlValidators: validator functions to apply
 *  formProp: FormControl identifier used for comparison with disableValidationForFields
 */
function formControlValidatorUpdate<PropType>(
	data: {
		abstractControl: AbstractControl,
		abstractControlValidators?: ValidatorFn[],
		formProp: PropType
	}
): void {
	const {
		abstractControl,
		abstractControlValidators,
		formProp
	} = { ...data };

	if ( !abstractControl || !formProp ) { return; }

	abstractControl.clearValidators();
	abstractControl.setValidators( abstractControlValidators );
	abstractControl.updateValueAndValidity( { onlySelf: true } );
}

/**
 * Inserts items into an array at a specific index
 *
 * @param form - the form to validate
 * @param logger - LoggerService instance
 * @param showErrorsOnInvalidFields - flag to outputs warn log on errors
 *
 * @return boolean
 */
function hasFormInvalidFields( form: UntypedFormGroup | UntypedFormArray, logger: LoggerService, showErrorsOnInvalidFields = true ): boolean {
	const invalidFields: string[] = findInvalidControlsRecursively( form );

	if ( invalidFields.length && showErrorsOnInvalidFields ) {
		// debug output
		logger.warn( 'invalidFields: ', invalidFields );
	}

	return !!invalidFields.length;
}

function hasRequiredValidator( formGroup: FormGroup, controlIdentifier: string ): boolean {
	if ( !formGroup?.controls || !controlIdentifier ) { return false; }

	const control: AbstractControl = formGroup?.get( controlIdentifier );
	return control.hasValidator( Validators.required );
}
